import { addScheduledMessage, removeScheduledMessage, updateScheduledMessage } from "../redux/Slice/Inbox/ScheduledMessageSlice";
import { fetchUserDetails, fetchUserPermissions } from "../redux/Slice/User/userDetailsSlice";
import { fetchLifecycleStagesWithCounts, applyLifecycleCountDelta } from "../redux/Slice/Lifecycle/lifecycleStageSlice";
import { invalidateCachedContact, setContactInfo } from "../redux/Slice/Inbox/inboxInfoSlice";
import { fetchLifecycleTabInbox, removeContactFromLifecycleList, invalidateLifecycleStageCache, updateLifecycleStageInInbox } from "../redux/Slice/Inbox/inboxRecentSlice";
import { soundUrlPushed } from "../redux/Slice/Settings/notificationSlice";
import connectionMonitor from "./connectionMonitor";
import toast from "react-hot-toast";
import {
  socketState,
  getDispatch,
  ensureDispatchReady,
  queueEvent,
  processEventQueue,
  getStore,
} from "./socketSharedState";
import {
  setupPendingMessagesInterval,
  cleanupPendingMessagesInterval,
} from "./socketPendingMessages";
import {
  handleReceiveMessage,
  handleMessageStatusUpdate,
  handleMessageReaction,
} from "./socketMessageDispatcher";
import {
  handleNewInboxMessage,
  handleAssignmentCountChanged,
  handleUserConnectedUpdate,
  handleUserDisconnectedUpdate,
  handleContactLabelsUpdated,
  handleForcedDisconnect,
  handleConnectionWarning,
  handleTeamMemberChatMessage,
  handleTeamMemberChatStatusUpdate,
  handleTeamMemberChatInboxUpdate,
  handleTaskChatMessage,
  handleTaskChatLogNotification,
  handleTaskChatMessageStatusClient,
  handleTaskUpdated,
  handleTaskDeleted,
} from "./socketInboxUpdater";

// ─── scheduled message handlers ───────────────────────────────────────────────
// These are kept here because they register directly on the socket instance.

const handleScheduledMessageCreated = (data) => {
  if (!ensureDispatchReady()) {
    queueEvent('scheduledMessageCreated', data, handleScheduledMessageCreated);
    return;
  }
  try {
    const dispatchFn = getDispatch();
    if (data.success && data.scheduledMessage) {
      dispatchFn(addScheduledMessage(data.scheduledMessage));
      toast.success('Message scheduled successfully');
    }
  } catch (error) {
    console.error('Error handling scheduledMessageCreated:', error);
  }
};

const handleScheduledMessageSent = (data) => {
  if (!ensureDispatchReady()) {
    queueEvent('scheduledMessageSent', data, handleScheduledMessageSent);
    return;
  }
  try {
    const dispatchFn = getDispatch();
    if (data.scheduledMessageId) {
      dispatchFn(removeScheduledMessage(data.scheduledMessageId));
      toast.success('Scheduled message sent successfully');
    }
  } catch (error) {
    console.error('Error handling scheduledMessageSent:', error);
  }
};

const handleScheduledMessageFailed = (data) => {
  if (!ensureDispatchReady()) {
    queueEvent('scheduledMessageFailed', data, handleScheduledMessageFailed);
    return;
  }
  try {
    const dispatchFn = getDispatch();
    if (data.scheduledMessageId) {
      dispatchFn(updateScheduledMessage({
        _id: data.scheduledMessageId,
        status: 'failed',
        errorMessage: data.reason,
      }));
      toast.error(`Scheduled message failed: ${data.reason || 'Unknown error'}`);
    }
  } catch (error) {
    console.error('Error handling scheduledMessageFailed:', error);
  }
};

const handleScheduledMessageCancelled = (data) => {
  if (!ensureDispatchReady()) {
    queueEvent('scheduledMessageCancelled', data, handleScheduledMessageCancelled);
    return;
  }
  try {
    const dispatchFn = getDispatch();
    if (data.success && data.scheduledMessageId) {
      dispatchFn(removeScheduledMessage(data.scheduledMessageId));
      toast.success('Scheduled message cancelled');
    }
  } catch (error) {
    console.error('Error handling scheduledMessageCancelled:', error);
  }
};

// Debounces the "contact moved INTO the currently-open Lifecycle stage" refetch
// below — several contacts can land in the same open stage close together (e.g.
// multiple independent AI conversations each classifying a different customer
// around the same time, not just one bulk action), and firing a full refetch per
// event would mean N network calls for one burst. Coalesces any such events
// arriving within LIFECYCLE_ADD_DEBOUNCE_MS of each other into a single fetch.
let lifecycleAddDebounceTimer = null;
const LIFECYCLE_ADD_DEBOUNCE_MS = 500;

// A role/permission change on the server pushes this with no payload — just means
// "your permissions changed, go get the fresh copy" (bypasses the client Map cache
// via force:true so it can't be skipped as a duplicate).
const handlePermissionsInvalidated = () => {
  if (!ensureDispatchReady()) {
    queueEvent('permissions:invalidate', null, handlePermissionsInvalidated);
    return;
  }
  try {
    const dispatchFn = getDispatch();
    dispatchFn(fetchUserDetails({ force: true }));
    dispatchFn(fetchUserPermissions({ force: true }));
  } catch (error) {
    console.error('Error handling permissions:invalidate:', error);
  }
};

// Lifecycle CRM: stage config changed (created/renamed/reordered/deleted) — no
// payload, just "go get the fresh list" (bare signal, matches the existing
// permissions:invalidate pattern above).
// Account-wide notification sound (Settings -> Notifications) changed by any
// team member on this account -- see emitNotificationSoundUpdated on the
// server. Patches Redux directly (payload already carries the new URL, no
// refetch needed); notificationUtils.js reacts to that state change itself.
const handleNotificationSoundUpdated = (data) => {
  if (!ensureDispatchReady()) {
    queueEvent('notification_sound_updated', data, handleNotificationSoundUpdated);
    return;
  }
  try {
    getDispatch()(soundUrlPushed({ soundUrl: data?.soundUrl ?? null, soundFileName: data?.soundFileName ?? null }));
  } catch (error) {
    console.error('Error handling notification_sound_updated:', error);
  }
};

const handleLifecycleStagesUpdated = () => {
  if (!ensureDispatchReady()) {
    queueEvent('lifecycle_stages_updated', null, handleLifecycleStagesUpdated);
    return;
  }
  try {
    // One merged call (stages + counts together) instead of two — stage
    // delete-with-reassignment shifts contact counts between stages, so the
    // count badges need the same live refresh as the stage list itself, not
    // just a reload, and both come back in the same round trip now.
    getDispatch()(fetchLifecycleStagesWithCounts({ force: true }));
  } catch (error) {
    console.error('Error handling lifecycle_stages_updated:', error);
  }
};

// Lifecycle CRM: a single contact's stage changed (manual move, AI action, or
// reopened-after-close). The contact detail panel (ChatInfo.jsx) reads from
// inboxInfoSlice's 2-tier cache (5min in-memory + 1hr IndexedDB), which the
// write path never touched — without this, reopening that contact within the
// cache window showed the stale pre-move stage. Bulk stage-deletion payloads
// (no single contactId) are skipped — rare admin action, self-corrects on the
// cache's natural TTL.
const handleContactLifecycleUpdated = (payload) => {
  if (!payload || payload.bulk || !payload.contactId) return;
  try {
    invalidateCachedContact(payload.contactId);
    // Same requeue-on-not-ready pattern as every other handler in this file
    // (handleLifecycleStagesUpdated etc.) — without it, an event landing in the
    // brief window after the socket connects but before initializeSocket()'s
    // dispatch wiring is ready was silently dropped instead of replayed once
    // ready, leaving the Lifecycle count badge and MessageCard's chip stale.
    if (!ensureDispatchReady()) {
      queueEvent('contact_lifecycle_updated', payload, handleContactLifecycleUpdated);
      return;
    }
    const dispatchFn = getDispatch();
    // A single move shifts one stage's count down and another's up. Patch both
    // in place from the payload instead of a full fetchLifecycleStageCounts
    // refetch — this event can fire on every AI/manual move, and a refetch here
    // meant every open tab in the workspace re-hit the server for one contact
    // moving; local math needs no network call at all.
    dispatchFn(applyLifecycleCountDelta({
      fromStageId: payload.fromStageId,
      toStageId: payload.toStageId,
    }));

    // Keep the Inbox list's own Lifecycle chip (MessageCard.jsx) accurate when the move
    // came from somewhere else (AI, a future chat-header dropdown) while this list is
    // open — pure local patch across the already-loaded buckets, no network call.
    dispatchFn(updateLifecycleStageInInbox({
      contactId: payload.contactId,
      lifecycleStageId: payload.toStageId || null,
    }));

    // Keep EVERY cached Lifecycle stage (InboxSettings.jsx rail click → per-stage
    // session cache in inboxRecent.lifecycleByStage, see fetchLifecycleTabInbox)
    // accurate in the background — not just whichever one happens to be on screen
    // right now. Reads state.inbox.selectedInboxTab (the single source of truth
    // InboxAll.jsx itself renders off of) to know which cached stage is the
    // currently-VISIBLE one, since that one gets a live refetch on arrival while
    // the others just get invalidated (see below).
    const store = getStore();
    const state = store?.getState?.();

    // Live-patch the OPEN chat's own contactInfo too (inboxInfoSlice) — the
    // list patch above never reached it, which is why the top navbar's
    // Lifecycle chip stayed on the old stage when the move came from
    // anywhere except itself (list "..." menu, AI, another agent) while
    // that contact was already open (Rahim, 2026-08-21 audit: "the
    // lifecycle from the top nav doesnt change"). Same setContactInfo
    // ChatInfo.jsx already uses for its own equivalent assignment-sync
    // listener, just gated here on this being the currently-open contact.
    if (String(state?.inboxInfo?.contactInfo?._id) === String(payload.contactId)) {
      dispatchFn(setContactInfo({ lifecycleStageId: payload.toStageId || null }));
    }

    const lifecycleByStage = state?.inboxRecent?.lifecycleByStage || {};
    const selectedTab = state?.inbox?.selectedInboxTab;
    const selectedStageId = typeof selectedTab === "string" && selectedTab.startsWith("lifecycle:")
      ? selectedTab.slice("lifecycle:".length)
      : null;

    // Moved OUT of a cached stage — we already know which id to drop, no network
    // call needed, whether or not that stage is the one currently on screen.
    if (payload.fromStageId && lifecycleByStage[payload.fromStageId]) {
      dispatchFn(removeContactFromLifecycleList({ contactId: payload.contactId, stageId: payload.fromStageId }));
    }

    if (payload.toStageId && String(payload.toStageId) === String(selectedStageId)) {
      // Moved IN to the stage currently on screen — the payload only has 3 ids,
      // not a full hydrated row (avatar/last message/status), so refresh it via a
      // small single-stage fetch rather than guessing at row data. Debounced (see
      // lifecycleAddDebounceTimer above) so several near-simultaneous moves into
      // this same stage collapse into one fetch instead of one per event. Must
      // force:true — this stage IS already cached (it's on screen), so without
      // force the thunk's own cache check would just skip it as a no-op.
      clearTimeout(lifecycleAddDebounceTimer);
      lifecycleAddDebounceTimer = setTimeout(() => {
        // Re-read the selected stage at fire-time, not the value captured when
        // this event arrived — the agent may have switched stages (or away from
        // Lifecycle entirely) during the debounce window, and firing a fetch for
        // a stage that's no longer open would just be a wasted call.
        const currentTab = getStore()?.getState?.()?.inbox?.selectedInboxTab;
        const currentStageId = typeof currentTab === "string" && currentTab.startsWith("lifecycle:")
          ? currentTab.slice("lifecycle:".length)
          : null;
        if (currentStageId === payload.toStageId && ensureDispatchReady()) {
          getDispatch()(fetchLifecycleTabInbox({ stageId: payload.toStageId, force: true }));
        }
      }, LIFECYCLE_ADD_DEBOUNCE_MS);
    } else if (payload.toStageId && lifecycleByStage[payload.toStageId]) {
      // Moved IN to a DIFFERENT stage that's cached but not the one on screen —
      // don't spend a background fetch keeping an invisible view live; just drop
      // its cache entry so the next time the agent actually opens it, one fresh
      // fetch happens (same cost as a first-ever visit, not more, and never stale).
      dispatchFn(invalidateLifecycleStageCache({ stageId: payload.toStageId }));
    }
  } catch (error) {
    console.error('Error handling contact_lifecycle_updated:', error);
  }
};

// ─── connection monitor wiring ────────────────────────────────────────────────

const setupConnectionMonitorHandlers = () => {
  connectionMonitor.setReceiveMessageHandler(handleReceiveMessage);
  connectionMonitor.setMessageStatusUpdateHandler(handleMessageStatusUpdate);
  connectionMonitor.setNewInboxMessageHandler(handleNewInboxMessage);
  connectionMonitor.setAssignmentCountChangedHandler(handleAssignmentCountChanged);
  connectionMonitor.setMessageReactionHandler(handleMessageReaction);
  connectionMonitor.setUserConnectedUpdateHandler(handleUserConnectedUpdate);
  connectionMonitor.setUserDisconnectedUpdateHandler(handleUserDisconnectedUpdate);
  connectionMonitor.setContactLabelsUpdatedHandler(handleContactLabelsUpdated);
  connectionMonitor.setForcedDisconnectHandler(handleForcedDisconnect);
  connectionMonitor.setConnectionWarningHandler(handleConnectionWarning);
  connectionMonitor.setTeamMemberChatMessageHandler(handleTeamMemberChatMessage);
  connectionMonitor.setTeamMemberChatStatusUpdateHandler(handleTeamMemberChatStatusUpdate);
  connectionMonitor.setTeamMemberChatInboxUpdateHandler(handleTeamMemberChatInboxUpdate);
  connectionMonitor.setTaskChatMessageHandler(handleTaskChatMessage);
  connectionMonitor.setTaskChatLogHandler(handleTaskChatLogNotification);
  connectionMonitor.setTaskChatMessageStatusHandler(handleTaskChatMessageStatusClient);
  connectionMonitor.setTaskUpdatedHandler(handleTaskUpdated);
  connectionMonitor.setTaskDeletedHandler(handleTaskDeleted);
};

// ─── public API ───────────────────────────────────────────────────────────────

export const initializeSocket = (userId, dispatchFn, accessToken = null) => {
  console.log('🚀 [SOCKET] initializeSocket CALLED with:', { userId, hasDispatch: !!dispatchFn, hasToken: !!accessToken });

  // Falls back to the globally-exposed store (see redux/store.js) instead of a static
  // import, since store.js -> authSlice.js -> socketHandler.js already form an import
  // cycle; importing store here too caused an intermittent
  // "Cannot access '__WEBPACK_DEFAULT_EXPORT__' before initialization" crash depending
  // on which module the bundler happened to touch first.
  const fallbackStore = typeof window !== 'undefined' ? window.store : undefined;
  socketState.dispatch = dispatchFn || (fallbackStore && fallbackStore.dispatch);
  console.log('🚀 [SOCKET] Dispatch set:', typeof socketState.dispatch === 'function');

  if (socketState.dispatch && typeof socketState.dispatch === 'function') {
    socketState.isDispatchReady = true;
    processEventQueue();
  } else if (fallbackStore && fallbackStore.dispatch && typeof fallbackStore.dispatch === 'function') {
    socketState.dispatch = fallbackStore.dispatch;
    socketState.isDispatchReady = true;
    processEventQueue();
  } else {
    socketState.isDispatchReady = false;
  }

  if (socketState.socket) {
    if (socketState.isDispatchReady) {
      processEventQueue();
    }
    return;
  }

  if (!accessToken) {
    const { getAccessToken } = require('./tokenManager');
    accessToken = getAccessToken();
  }

  setupConnectionMonitorHandlers();

  connectionMonitor.initializeSocket(userId, dispatchFn, accessToken);

  socketState.socket = connectionMonitor.getSocket();

  if (socketState.socket) {
    socketState.socket.on('scheduledMessageCreated', handleScheduledMessageCreated);
    socketState.socket.on('scheduledMessageSent', handleScheduledMessageSent);
    socketState.socket.on('scheduledMessageFailed', handleScheduledMessageFailed);
    socketState.socket.on('scheduledMessageCancelled', handleScheduledMessageCancelled);
    socketState.socket.on('permissions:invalidate', handlePermissionsInvalidated);
    socketState.socket.on('lifecycle_stages_updated', handleLifecycleStagesUpdated);
    socketState.socket.on('contact_lifecycle_updated', handleContactLifecycleUpdated);
    socketState.socket.on('notification_sound_updated', handleNotificationSoundUpdated);
  }

  // Start pending message timeout monitor (cleans up stale intervals on reconnect)
  setupPendingMessagesInterval();
};

export const getSocket = () => {
  if (!socketState.socket) {
    throw new Error("Socket connection not initialized! Call initializeSocket() first.");
  }
  return socketState.socket;
};

export const getSocketId = () => {
  if (!socketState.socket) {
    throw new Error("Socket connection not initialized! Call initializeSocket() first.");
  }
  return socketState.socket.id;
};

export const disconnectSocket = () => {
  if (socketState.socket) {
    // Remove the listeners added in initializeSocket so they don't accumulate
    // across reconnect cycles.
    socketState.socket.off('scheduledMessageCreated', handleScheduledMessageCreated);
    socketState.socket.off('scheduledMessageSent', handleScheduledMessageSent);
    socketState.socket.off('scheduledMessageFailed', handleScheduledMessageFailed);
    socketState.socket.off('scheduledMessageCancelled', handleScheduledMessageCancelled);
    socketState.socket.off('permissions:invalidate', handlePermissionsInvalidated);
    cleanupPendingMessagesInterval();
    connectionMonitor.disconnect();
    socketState.socket = null;
  }
};

// ─── connection monitor passthrough exports ───────────────────────────────────

export const getConnectionStatus = () => connectionMonitor.getConnectionStatus();
export const retryConnection = () => connectionMonitor.retryConnection();
export const onConnectionChange = (callback) => connectionMonitor.on('connection', callback);
export const onDisconnectionChange = (callback) => connectionMonitor.on('disconnection', callback);
export const onReconnectFailedChange = (callback) => connectionMonitor.on('reconnectFailed', callback);
export const onReconnectingChange = (callback) => connectionMonitor.on('reconnecting', callback);

// ─── re-exports for consumers that import from socketHandler ──────────────────

export { setActiveChat, clearActiveChat, getActiveChat } from "./socketSharedState";
export { trackPendingMessage } from "./socketPendingMessages";
